import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  query,
  getDoc,
  orderBy,
} from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import "./adminpanel.css";

const OrderManagement = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [userAddresses, setUserAddresses] = useState({});

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const q = query(collection(db, "users"));
        const querySnapshot = await getDocs(q);
        const allOrders = {};
        const addresses = {};

        for (const userDoc of querySnapshot.docs) {
          const userId = userDoc.id;
          const userRef = doc(db, "users", userId);
          const userSnap = await getDoc(userRef);

          if (userSnap.exists()) {
            addresses[userId] = userSnap.data().address || "No address available";
          }

          const ordersRef = collection(db, "users", userId, "orders");
          const ordersSnapshot = await getDocs(ordersRef);

          ordersSnapshot.forEach((orderDoc) => {
            allOrders[orderDoc.id] = {
              ...orderDoc.data(),
              orderId: orderDoc.id,
              userId: userId,
            };
          });
        }

        const sortedOrders = Object.values(allOrders).sort(
          (a, b) => b.timestamp?.toMillis() - a.timestamp?.toMillis()
        );

        setOrders(sortedOrders);
        setUserAddresses(addresses);
      } catch (error) {
        console.error("Error fetching orders:", error);
      }
    };

    fetchOrders();
  }, []);

  const updateUserProfile = async (userId) => {
    try {
      const userRef = doc(db, "users", userId);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const userData = userSnap.data();
        const updatedOrders = await getDocs(collection(db, "users", userId, "orders"));
        const updatedOrderList = updatedOrders.docs.map((doc) => doc.data());

        await updateDoc(userRef, { orders: updatedOrderList });
      }
    } catch (error) {
      console.error("Error updating user profile:", error);
    }
  };

  const handleStatusChange = async (orderId, userId, isChecked) => {
    try {
      const orderRef = doc(db, "users", userId, "orders", orderId);
      await updateDoc(orderRef, { status: isChecked ? "Delivered" : "Pending" });

      setOrders((prevOrders) =>
        prevOrders.map((order) =>
          order.orderId === orderId ? { ...order, status: isChecked ? "Delivered" : "Pending" } : order
        )
      );

      await updateUserProfile(userId);
    } catch (error) {
      console.error("Error updating order status:", error);
    }
  };

  const handlePaymentStatusChange = async (orderId, userId, isChecked) => {
    try {
      const orderRef = doc(db, "users", userId, "orders", orderId);
      await updateDoc(orderRef, { paymentStatus: isChecked ? "Paid" : "Pending" });

      setOrders((prevOrders) =>
        prevOrders.map((order) =>
          order.orderId === orderId ? { ...order, paymentStatus: isChecked ? "Paid" : "Pending" } : order
        )
      );

      await updateUserProfile(userId);
    } catch (error) {
      console.error("Error updating payment status:", error);
    }
  };

  const handleOrderCancellation = async (orderId, userId, itemIndex = null) => {
    try {
      const orderRef = doc(db, "users", userId, "orders", orderId);
      const orderSnap = await getDoc(orderRef);

      if (orderSnap.exists()) {
        const orderData = orderSnap.data();

        if (itemIndex !== null) {
          orderData.items[itemIndex].status = "Canceled";
        } else {
          orderData.status = "Canceled";
          orderData.items.forEach((item) => (item.status = "Canceled"));
        }

        await updateDoc(orderRef, orderData);

        setOrders((prevOrders) =>
          prevOrders.map((order) =>
            order.orderId === orderId ? { ...orderData, orderId, userId } : order
          )
        );

        await updateUserProfile(userId);
      }
    } catch (error) {
      console.error("Error canceling order:", error);
    }
  };

  return (
    <div className="admin-panel">
      <h2>Order Management</h2>
      {orders.length === 0 ? (
        <p>No orders available.</p>
      ) : (
        <div className="order-list">
          {orders.map((order, index) => (
            <div key={index} className={`order-card ${order.status === "Canceled" ? "order-canceled" : ""}`}>
              <h3>Order ID: {order.orderId}</h3>
              <p>User: {order.userEmail}</p>
              <p>Order Type: {order.orderType}</p>
              <p>Address: {userAddresses[order.userId] || "No address available"}</p>
              <p>Payment Method: {order.paymentMethod}</p>
              <p>Payment Status: {order.paymentStatus}</p>
              <p>Status: {order.status}</p>
              <p>Total Amount: ₹{order.totalAmount}</p>
              <p><strong>Delivery Address:</strong> {userAddresses[order.userId] || "No address available"}</p>

              <h5>Items:</h5>
              {order.items.map((item, idx) => (
                <div key={idx} className={`order-item ${item.status === "Canceled" ? "item-canceled" : ""}`}>
                  <p>{item.name} - {item.quantity} x ₹{item.price} = ₹{item.totalPrice}</p>
                  {item.status !== "Canceled" && (
                    <button
                      className="cancel-item-btn"
                      onClick={() => handleOrderCancellation(order.orderId, order.userId, idx)}
                    >
                      Cancel Item
                    </button>
                  )}
                </div>
              ))}

              <button
                className="cancel-order-btn"
                onClick={() => handleOrderCancellation(order.orderId, order.userId)}
              >
                Cancel Order
              </button>

              <label>
                <input
                  type="checkbox"
                  checked={order.status === "Delivered"}
                  onChange={(e) => handleStatusChange(order.orderId, order.userId, e.target.checked)}
                />
                Mark as Delivered
              </label>

              <label>
                <input
                  type="checkbox"
                  checked={order.paymentStatus === "Paid"}
                  onChange={(e) => handlePaymentStatusChange(order.orderId, order.userId, e.target.checked)}
                />
                Mark as Paid
              </label>
            </div>
          ))}
        </div>
      )}

      <style jsx>{`
        .order-card {
          border: 1px solid #ccc;
          padding: 10px;
          margin: 10px 0;
          border-radius: 5px;
          background-color: #f9f9f9;
        }
        .order-canceled {
          background-color: #ffe6e6;
          border-left: 4px solid #ff4d4d;
        }
        .order-item {
          margin-left: 10px;
          padding: 5px;
        }
        .item-canceled {
          color: #ff4d4d;
          text-decoration: line-through;
        }
        .cancel-order-btn, .cancel-item-btn {
          background-color: #ff4d4d;
          color: white;
          border: none;
          padding: 5px 10px;
          margin: 5px;
          cursor: pointer;
          border-radius: 3px;
        }
        .cancel-order-btn:hover, .cancel-item-btn:hover {
          background-color: #cc0000;
        }
      `}</style>
    </div>
  );
};

export default OrderManagement;
